import React, { useState, useEffect } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { MdClose, MdLocalOffer } from 'react-icons/md';
import { Post } from '../api/server';

function PromoCodeInput({ eventId, onPromoApplied, onPromoRemoved, externalPromo }) {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (externalPromo && !appliedPromo) {
      setAppliedPromo(externalPromo);
      setCode('');
      setError('');
    } else if (!externalPromo && appliedPromo) {
      setAppliedPromo(null);
      setCode('');
      setError('');
    }
  }, [externalPromo]);

  async function validateCode(promoCode) {
    const trimmed = (promoCode || '').trim().toUpperCase();
    if (!trimmed || !eventId) return;

    setIsValidating(true);
    setError('');

    try {
      const response = await Post('/promocodes/validate', {
        code: trimmed,
        eventId,
      });

      if (response.valid) {
        const promoState = {
          promoCodeId: response.promoCodeId,
          promoCode: trimmed,
          discount_type: response.discount_type,
          discount_value: response.discount_value,
          applies_to: response.applies_to,
          price_tiers: response.price_tiers || [],
          ticket_inventories: response.ticket_inventories || [],
          affiliateId: response.affiliateId || null,
          affiliateName: response.affiliateName || null,
          max_per_user: response.max_per_user,
        };
        setAppliedPromo(promoState);
        setCode('');
        onPromoApplied(promoState);
      } else {
        setError(response.error || 'Invalid promo code');
      }
    } catch (err) {
      const msg = err?.responseJSON?.error || err?.responseJSON?.message || 'Could not validate promo code';
      setError(msg);
    } finally {
      setIsValidating(false);
    }
  }

  function handleApply() {
    validateCode(code);
  }

  function handleRemove() {
    setAppliedPromo(null);
    setError('');
    setCode('');
    onPromoRemoved();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  }

  const discountLabel =
    appliedPromo?.discount_type === 'percent'
      ? `${appliedPromo.discount_value}% off`
      : `$${appliedPromo?.discount_value?.toFixed(2)} off`;

  if (appliedPromo) {
    return (
      <div className="d-flex align-items-center justify-content-between rounded px-2 py-2"
        style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb' }}>
        <div className="d-flex align-items-center gap-2 min-w-0">
          <MdLocalOffer size={16} style={{ color: '#28a745', flexShrink: 0 }} />
          <span className="text-success small text-truncate">
            <span className="fw-semibold">{appliedPromo.promoCode}</span> &mdash; {discountLabel}
          </span>
        </div>
        <button
          type="button"
          className="btn btn-link p-0 ms-2 flex-shrink-0"
          onClick={handleRemove}
          aria-label="Remove promo code"
          title="Remove promo code"
        >
          <MdClose size={16} className="text-danger" />
        </button>
      </div>
    );
  }

  return (
    <div className="promo-code-section">
      <label className="small text-muted mb-1 d-block">Have a promo code?</label>
      <div className="d-flex align-items-center gap-2">
        <Form.Control
          size="sm"
          type="text"
          placeholder="Enter code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            if (error) setError('');
          }}
          onKeyDown={handleKeyDown}
          disabled={isValidating}
          className="flex-grow-1"
          style={{ textTransform: 'uppercase' }}
        />
        <button
          type="button"
          className="btn btn-sm btn-dark flex-shrink-0"
          onClick={handleApply}
          disabled={isValidating || !code.trim()}
          style={{ minWidth: 70 }}
        >
          {isValidating ? <Spinner animation="border" size="sm" /> : 'Apply'}
        </button>
      </div>
      {error && (
        <div className="text-danger small mt-1">{error}</div>
      )}
    </div>
  );
}

export default PromoCodeInput;
